import React, { useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { CameraView } from "expo-camera";
import i18n from "../../locales/i18n";

interface CameraScannerProps {
  OpenCam: boolean;
  SetOpenCam: (open: boolean) => void;
  handelScanProduct: (code: string) => void;
  scanning: boolean;
  locale: string;
  showText?: boolean;
}

const CameraScanner: React.FC<CameraScannerProps> = ({
  OpenCam,
  SetOpenCam,
  handelScanProduct,
  scanning,
  locale,
  showText = true,
}) => {
  const [cameraView, setCameraView] = useState<"front" | "back">("back");
  const [lastTap, setLastTap] = useState<number>(0);

  // Helper to easily check current state
  const isFront = cameraView === "front";

  const handleDoubleTap = () => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // milliseconds

    if (lastTap && now - lastTap < DOUBLE_TAP_DELAY) {
      // Double tap detected - flip camera
      setCameraView((prev) => (prev === "back" ? "front" : "back"));
      setLastTap(0); // Reset
    } else {
      // First tap
      setLastTap(now);
    }
  };

  return (
    <View>
      {!OpenCam && showText && (
        <TouchableOpacity
          onPress={() => {
            SetOpenCam(true);
          }}
        >
          <Text
            className={`${
              locale == "fr" ? "text-left" : "text-right"
            }   text-xl   underline underline-offset-4 font-bold py-2 px-4 `}
          >
            {i18n.t("openCamera")}
          </Text>
        </TouchableOpacity>
      )}

      {OpenCam && (
        <>
          <TouchableOpacity
            activeOpacity={1}
            onPress={handleDoubleTap}
            style={{ backgroundColor: "black", paddingVertical: 16 }}
          >
            <View style={{ position: "relative", height: 240, width: "100%" }}>
              <CameraView
                style={{ height: 240, width: "100%" }}
                facing={cameraView}
                autofocus="on"
                // FIX 1: Apply digital zoom for front camera
                // This lets users hold items further away (in focus) while still seeing them close up
                zoom={isFront ? 0.2 : 0}
                barcodeScannerSettings={{
                  barcodeTypes: [
                    "ean13",
                    "ean8",
                    "upc_a",
                    "upc_e",
                    "code128",
                    "code39",
                  ],
                }}
                onBarcodeScanned={(data) => {
                  if (!scanning && data?.data) {
                    // FIX 2: Bypass frame calculations for Front Camera
                    // Front cameras mirror coordinates, making bounds checks unreliable.
                    // It is better to just accept the scan if the camera sees it.
                    if (isFront) {
                      handelScanProduct(data.data);
                      return;
                    }

                    // Logic for Back Camera (Frame constraint)
                    const { bounds } = data;
                    if (bounds && bounds.origin && bounds.size) {
                      const cameraHeight = 200;
                      const cameraWidth = 350;

                      const frameWidth = 250;
                      const frameHeight = 150;
                      const frameX = (cameraWidth - frameWidth) / 2;
                      const frameY = (cameraHeight - frameHeight) / 2;

                      const barcodeCenterX =
                        bounds.origin.x + bounds.size.width / 2;
                      const barcodeCenterY =
                        bounds.origin.y + bounds.size.height / 2;

                      const isWithinFrame =
                        barcodeCenterX >= frameX &&
                        barcodeCenterY >= frameY &&
                        barcodeCenterX <= frameX + frameWidth &&
                        barcodeCenterY <= frameY + frameHeight;

                      const tolerance = 30;
                      const isNearFrame =
                        Math.abs(barcodeCenterX - (frameX + frameWidth / 2)) <=
                          frameWidth / 2 + tolerance &&
                        Math.abs(barcodeCenterY - (frameY + frameHeight / 2)) <=
                          frameHeight / 2 + tolerance;

                      if (isWithinFrame || isNearFrame) {
                        handelScanProduct(data.data);
                      }
                    } else {
                      handelScanProduct(data.data);
                    }
                  }
                }}
              />

              {/* Scanning overlay */}
              <View style={styles.overlay} pointerEvents="none">
                <View style={styles.darkOverlay} />

                <View style={styles.textContainer}>
                  <Text style={styles.scannerText}>
                    {i18n.t("barcodeReading")}
                  </Text>
                  <Text style={styles.instructionText}>
                    {/* FIX 3: Update text to guide user when using front camera */}
                    {isFront
                      ? "Hold item further away"
                      : i18n.t("doubleTapToFlip") ||
                        "Double tap to flip camera"}
                  </Text>
                </View>
              </View>
            </View>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              SetOpenCam(false);
            }}
          >
            <Text
              className={`${
                locale == "fr" ? "text-left" : "text-right"
              }   text-xl   underline underline-offset-4 font-bold px-4 py-2 `}
            >
              {i18n.t("closeCamera")}
            </Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "transparent",
    margin: 64,
  },
  button: {
    flex: 1,
    alignSelf: "flex-end",
    alignItems: "center",
  },
  text: {
    fontSize: 24,
    fontWeight: "bold",
    color: "white",
  },
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  darkOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  scannerFrame: {
    width: 250,
    height: 150,
    backgroundColor: "transparent",
    borderRadius: 15,
    position: "absolute",
    top: 75,
    alignSelf: "center",
    zIndex: 2,
    borderWidth: 2,
    borderColor: "#00FF00",
  },
  cornerTopLeft: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 20,
    height: 20,
    borderTopWidth: 3,
    borderLeftWidth: 3,
    borderColor: "#00FF00",
    borderTopLeftRadius: 15,
  },
  cornerTopRight: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 20,
    height: 20,
    borderTopWidth: 3,
    borderRightWidth: 3,
    borderColor: "#00FF00",
    borderTopRightRadius: 15,
  },
  cornerBottomLeft: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 20,
    height: 20,
    borderBottomWidth: 3,
    borderLeftWidth: 3,
    borderColor: "#00FF00",
    borderBottomLeftRadius: 15,
  },
  cornerBottomRight: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 20,
    height: 20,
    borderBottomWidth: 3,
    borderRightWidth: 3,
    borderColor: "#00FF00",
    borderBottomRightRadius: 15,
  },
  scannerText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
    marginTop: 20,
    textAlign: "center",
    zIndex: 1,
  },
  instructionText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: 14,
    marginTop: 5,
    textAlign: "center",
    zIndex: 1,
  },
  textContainer: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 3,
  },
  warningContainer: {
    backgroundColor: "rgba(255, 165, 0, 0.9)",
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 10,
    marginHorizontal: 20,
  },
  warningText: {
    color: "white",
    fontSize: 13,
    fontWeight: "600",
    textAlign: "center",
  },
  warningSubtext: {
    color: "white",
    fontSize: 11,
    textAlign: "center",
    marginTop: 4,
    opacity: 0.9,
  },
});

export default CameraScanner;
